import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

interface SelectMAIQProps {
  label: string;
  value: string | string[];
  options: { [key: string]: any }[];
  onChange: (event: any) => void;
  isMulti?: boolean;
  disabled?: boolean;
  renderValue?: string;
  selectedValue?: string;
  searchable?: boolean;
}

const SelectMAIQ: React.FC<SelectMAIQProps> = ({
  label,
  value,
  options,
  onChange,
  isMulti = false,
  disabled = false,
  renderValue = "name",
  selectedValue = "_id",
  searchable = true
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!disabled && options.length > 0) {
      if ((isMulti && (value as string[]).length === 0) || (!isMulti && !value)) {
        const firstVal = isMulti
          ? [options[0][selectedValue]]
          : options[0][selectedValue];
        onChange({ target: { value: firstVal } });
      }
    }
  }, [options]);

  const getLabelByValue = (val: string) => {
    const found = options.find((opt) => opt[selectedValue] === val);
    return found ? found[renderValue] : val;
  };

  const isValidValue = (val: string | string[]) => {
    if (!val) return false;
    if (isMulti) {
      return (val as string[]).every(v => options.some(opt => opt[selectedValue] === v));
    }
    return options.some(opt => opt[selectedValue] === val);
  };

  const safeValue = isValidValue(value) ? value : isMulti ? [] : '';

  // Filter options based on search term
  const filteredOptions = searchable 
    ? options.filter(option => 
        option[renderValue].toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  if (options.length === 1) {
    return (
      <FormControl fullWidth margin="normal" disabled={true}>
        <InputLabel shrink>{label}</InputLabel>
        <Box
          sx={{
            height: "56px",
            display: "flex",
            alignItems: "center",
            px: 2,
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: 1,
            backgroundColor: "#f5f5f5",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          {options[0][renderValue]}
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth margin="normal" disabled={disabled}>
      {/* <InputLabel>{label}</InputLabel> */}
      <Select
        size="small"
        multiple={isMulti}
        value={safeValue}
        onChange={onChange}
        IconComponent={KeyboardArrowDownIcon}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        renderValue={(selected) =>
          isMulti ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((val) => (
                <Chip
                  key={val}
                  label={getLabelByValue(val)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDelete={() =>
                    onChange({
                      target: {
                        value: (value as string[]).filter((v) => v !== val),
                      },
                    })
                  }
                  deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
                />
              ))}
            </Box>
          ) : (
            getLabelByValue(selected as string)
          )
        }
        disabled={disabled}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        {searchable && (
          <Box 
            sx={{ 
              p: 1, 
              borderBottom: '1px solid #e0e0e0',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <TextField
              size="small"
              placeholder="Search options..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
        )}
        {filteredOptions && filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={option[selectedValue]} value={option[selectedValue]}>
              {option[renderValue]}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            {searchTerm ? "No options found" : "No options available"}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default SelectMAIQ;
